import { gql, makeVar, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { useCurrent } from '@/hooks/useCurrent';

import {
  UseAutoPollVote_UpsertPollResponseForCurrentUser,
  UseAutoPollVote_UpsertPollResponseForCurrentUserVariables,
} from './graphql-types/UseAutoPollVote_UpsertPollResponseForCurrentUser';

const UseAutoPollVote_UpsertPollResponseForCurrentUserAST = gql`
  mutation UseAutoPollVote_UpsertPollResponseForCurrentUser(
    $input: UpsertPollResponseForCurrentUserInput!
  ) {
    upsertPollResponseForCurrentUser(input: $input) {
      data {
        _id
      }
    }
  }
`;

const calledVar = makeVar(false);

export const useAutoPollVote = (options: { validRoutes: string[] }): void => {
  const toast = useToast();
  const [upsertPollResponse] = useMutation<
    UseAutoPollVote_UpsertPollResponseForCurrentUser,
    UseAutoPollVote_UpsertPollResponseForCurrentUserVariables
  >(UseAutoPollVote_UpsertPollResponseForCurrentUserAST);
  const { validRoutes } = options;
  const router = useRouter();

  const isValidRoute = validRoutes.reduce(
    (prev, route) => prev || router.asPath.includes(route),
    false
  );
  const current = useCurrent();

  React.useEffect(() => {
    const optionId = router.query.voteOnPoll;
    if (
      !router.isReady ||
      !optionId ||
      !isValidRoute ||
      !current.user?._id ||
      calledVar() ||
      typeof optionId !== 'string'
    ) {
      return;
    }
    calledVar(true);

    upsertPollResponse({
      variables: { input: { data: { optionId } } },
    })
      .then(() => {
        toast({ status: 'success', description: 'Successfully voted on poll' });
      })
      .catch(() =>
        toast({
          status: 'error',
          description: 'An error occurred while voting on poll',
        })
      )
      .finally(() => {
        const { voteOnPoll, ...queryWithoutVoteOnPoll } = router.query;
        router.replace({ query: queryWithoutVoteOnPoll });
      });
  }, [current.user?._id, isValidRoute, upsertPollResponse, router, toast]);
};
